import { Button, Box, FormControl } from '@/components'
import { Borrower, Mortgage, PropertyItem } from '@/model'
import { MortgageValuation } from '@/model/types/Mortgage'
import { useEffect, useState } from 'react'
import { NumberUtil, SelectUtil } from '@/util'
import FileGrid from './FileGrid'
import MessageGrid from './MessageGrid'
import { LOAN_TYPE_LIST } from '@/constants/prop/LoanType'
import { BORROWER_TYPE_LIST } from '@/constants/prop/BorrowerType'
import { MORTGAGE_BOND_TYPE_LIST, MORTGAGE_PERSONAL_TYPE_LIST } from '@/constants/prop/MortgageType'
import { PropertyDocumentFile, PropertyItemInvestor } from '@/model/types/PropertyItem'

function Proposal ( props: ProposalProps ) {
    const {
        data, noMessageGrid = false, isUploadAuth = true,
        userTel,
        onCofirmClick, onSendClick
    } = props
    const { propertyItem, mortgage, valuation, borrower } = data || {}

    const [ fileData, setFileData ] = useState( propertyItem?.documentFiles || [] )
    const [ messageData, setMessageData ] = useState( propertyItem?.investors || [] )

    useEffect( () => {
        setFileData( propertyItem?.documentFiles
            .filter( r => r.mainYn === 'Y' || r.telNo === userTel ) || []
        )
        setMessageData( propertyItem?.investors
            .filter( r => userTel ? r.telNo === userTel : true ) || []
        )
    }, [ propertyItem ] )

    const onSaveByInvestorAndDocumentFile: () => void = () => {
        propertyItem.addInvestors( messageData )
        propertyItem.addDocumentFiles( fileData )
    }

    const onCustomSendClick: () => void = () => {
        if ( !validateSaveByInvestorInfo() ) return

        onSaveByInvestorAndDocumentFile()
        onSendClick( fileData, messageData )

    }

    const validateSaveByInvestorInfo: () => boolean = () => {
        let valueCheck = true

        if ( messageData.length === 0 || messageData.some( ( { investNm, telNo } ) => !investNm || !telNo ) ) {
            alert( '발송할 대상 정보를 입력하세요.' )
            valueCheck = false
        }

        return valueCheck
    }

    return(
        <>
            <Box sx={ { marginBottom: '18px' } }>
                <table className="table-grid">
                    <colgroup>
                        <col width="118px"/>
                        <col width="*"/>
                    </colgroup>
                    <thead>
                        <tr><th colSpan={2} style={{ textAlign:'center', fontSize: '18px' }}>대출개요</th></tr>
                        <tr>
                            <th style={{ textAlign: 'center' }}>구분</th>
                            <th style={{ textAlign: 'center' }}>내용</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>대출과목</th>
                            <td>
                                { SelectUtil.create( LOAN_TYPE_LIST ).printName( propertyItem?.advisor?.loanType ) }
                            </td>
                        </tr>
                        <tr>
                            <th>대출금액</th>
                            <td>
                                { NumberUtil.formatNumber( propertyItem?.advisor?.loanAmount ) + ' 원'}
                            </td>
                        </tr>
                        <tr>
                            <th>대출기간</th>
                            <td>
                                { propertyItem?.advisor?.loanTerm || '' + ' 개월' }
                            </td>
                        </tr>
                        <tr>
                            <th>금리</th>
                            <td>
                                { NumberUtil.formatNumber( propertyItem?.advisor?.loanRate, 2 ) + ' %' }
                            </td>
                        </tr>
                        <tr>
                            <th>수수료</th>
                            <td>
                                { NumberUtil.formatNumber( propertyItem?.advisor?.loanFee, 2 ) + ' %' }
                            </td>
                        </tr>
                        <tr>
                            <th>비고</th>
                            <td>
                                { propertyItem?.advisor?.loanRemark }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Box>
            <Box sx={ { marginBottom: '18px' } }>
                <table className="table-grid">
                    <colgroup>
                        <col width="118px"/>
                        <col width="*"/>
                    </colgroup>
                    <thead>
                        <tr><th colSpan={2} style={{ textAlign:'center', fontSize: '18px' }}>차주 정보</th></tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>{ SelectUtil.create( BORROWER_TYPE_LIST ).printName( borrower?.borrowerType ) }</th>
                            <td>
                                { propertyItem?.overallOpinion }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Box>
            <Box sx={ { marginBottom: '18px' } }>
                <table className="table-grid">
                    <colgroup>
                        <col width="118px"/>
                        <col width="*"/>
                    </colgroup>
                    <thead>
                        <tr><th colSpan={2} style={{ textAlign:'center', fontSize: '18px' }}>채권 보전</th></tr>
                        <tr><th>구분</th><th>내용</th></tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>물적담보</th>
                            <td>
                                { SelectUtil.create( MORTGAGE_BOND_TYPE_LIST ).printName( propertyItem?.advisor?.mortgageBond ) }
                            </td>
                        </tr>
                        <tr>
                            <th>인적담보</th>
                            <td>
                                { SelectUtil.create( MORTGAGE_PERSONAL_TYPE_LIST ).printName( propertyItem?.advisor?.personalSecurity ) }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Box>
            <Box sx={ { marginBottom: '18px' } }>
                <table className="table-grid">
                    <colgroup>
                        <col width="118px"/>
                        <col width="*"/>
                    </colgroup>
                    <thead>
                        <tr><th colSpan={2} style={{ textAlign:'center', fontSize: '18px' }}>담보개요</th></tr>
                        <tr><th>구분</th><th>내용</th></tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>주소</th>
                            <td>
                                { propertyItem?.addressDetail }
                            </td>
                        </tr>
                        <tr>
                            <th>면적</th>
                            <td>
                                { NumberUtil.formatNumber( valuation?.propertyM2, 2 ) + ' m²'}
                            </td>
                        </tr>
                        <tr>
                            <th>감정가</th>
                            <td>
                                { NumberUtil.formatNumber( valuation?.appraisalValue ) + ' 원' }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Box>
            <FileGrid
                data={ fileData }
                isNonDisplayTelNo={ true }
                isUploadAuth={ isUploadAuth }
                setData={ setFileData }
            />
            {/* { !noMessageGrid && <MessageGrid data={ messageData } setData={ setMessageData }/> } */}
            <Box sx={ { width: '100%', textAlign: 'center' } }>
                <FormControl>
                    {/* { onSendClick &&
                        <Button
                            sx={ { width: '250px' } }
                            onClick={ onCustomSendClick }
                        >
                            발송
                        </Button>
                    } */}
                    <Button
                        sx={ { mt: 2, width:'250px' } }
                        onClick={ () => {
                            onCofirmClick()
                        } }
                    >
                        확인
                    </Button>
                </FormControl>
            </Box>
        </>
    )
}

export default Proposal

export interface ProposalProps {
    data: ProposalData
    popup?: boolean
    isUploadAuth?: boolean
    noMessageGrid?: boolean
    userTel?: string
    onSendClick?: ( files: PropertyDocumentFile[], invstors: PropertyItemInvestor[] ) => void
    onCofirmClick?: () => void
}

export type ProposalData = {
    propertyItem: PropertyItem
    mortgage: Mortgage
    valuation: MortgageValuation
    borrower: Borrower
}